import { useRef, useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AutogrowTextarea from "../ui/autogrowtextarea";
import "./TwoDGauge.css";
import LoggerContext from "../../context/LoggerProvider";

const TwoDGauge = ({
  itemkey,
  content,
  onChange,
  defaultValue,
  value: valueProp = undefined,
  label,
  xAxisLabel = "Internal",
  yAxisLabel = "External",
  enableFeedback = true,
  lowProgressZone = { x: 20, y: 20, operator: "AND" },
  highProgressZone = { x: 75, y: 75, operator: "OR" },
  lowProgressMessage,
  highProgressMessage,
  placeholderMessage,
  /** Compact layout for Review/Example view - smaller, no horizontal scroll */
  compact = false,
  /** Read-only mode: no interaction, show selected point only */
  readOnly = false,
}) => {
  const canvasRef = useRef(null);

  const { t } = useTranslation();

  // Set default messages using i18n if not provided
  const finalLowProgressMessage =
    lowProgressMessage !== undefined
      ? lowProgressMessage
      : t("low_zone_default_message") || "Point is in the low zone";
  const finalHighProgressMessage =
    highProgressMessage !== undefined
      ? highProgressMessage
      : t("high_zone_default_message") || "Point is in the high zone";
  const finalPlaceholderMessage =
    placeholderMessage !== undefined
      ? placeholderMessage
      : t("click_gauge_to_select") || "Click on the gauge";

  const [textAreaLabel, setTextAreaLabel] = useState("");
  const [textAreaVisible, setTextAreaVisible] = useState(false);

  const isControlled = valueProp !== undefined && valueProp !== null;
  const [internalValue, setInternalValue] = useState(defaultValue || {});
  const value = isControlled ? (valueProp || {}) : internalValue;

  const { Logger } = useContext(LoggerContext);

  // Helper function to check if values are in a zone
  // For low zone: comparison is '<', values represent maximum thresholds (zone active when x ≤ maxX AND/OR y ≤ maxY)
  // For high zone: comparison is '>', values represent minimum thresholds (zone active when x ≥ minX AND/OR y ≥ minY)
  const isInZone = (xValue, yValue, zone, comparison) => {
    const xCondition = comparison === "<" ? xValue < zone.x : xValue > zone.x;
    const yCondition = comparison === "<" ? yValue < zone.y : yValue > zone.y;

    if (zone.operator === "AND") {
      return xCondition && yCondition;
    } else if (zone.operator === "OR") {
      return xCondition || yCondition;
    }
    return false;
  };

  const handleCanvasValue = (offsetX, offsetY, xValue, yValue) => {
    let context = canvasRef.current.getContext("2d");
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    let selectedColor = {};

    let cyan = yValue;
    let yellow = xValue;
    let magenta = 0;
    let kontrast = 0;

    selectedColor.r = Math.floor(255 * (1 - cyan / 100) * (1 - kontrast / 100));
    selectedColor.g = Math.floor(
      255 * (1 - magenta / 100) * (1 - kontrast / 100)
    );
    selectedColor.b = Math.floor(
      255 * (1 - yellow / 100) * (1 - kontrast / 100)
    );

    context.fillStyle = `rgba(${selectedColor.r}, ${selectedColor.g}, ${
      selectedColor.b
    }, ${Math.max(xValue, yValue) / 100})`;
    context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    context.beginPath();
    context.arc(offsetX, offsetY, 3, 0, 2 * Math.PI, false);
    context.fillStyle = "#5890c3";
    context.fill();

    // Only show feedback if enabled
    if (enableFeedback) {
      const inLowProgressZone = isInZone(xValue, yValue, lowProgressZone, "<");
      const inHighProgressZone = isInZone(
        xValue,
        yValue,
        highProgressZone,
        ">"
      );

      if (inLowProgressZone || inHighProgressZone) {
        setTextAreaVisible(true);
        if (inLowProgressZone) setTextAreaLabel(finalLowProgressMessage);
        else setTextAreaLabel(finalHighProgressMessage);
      } else {
        setTextAreaVisible(false);
      }
    } else {
      setTextAreaVisible(false);
    }
  };

  const handleCanvasClick = (e) => {
    if (readOnly) return;
    if (!!onChange) {
      let rect = canvasRef.current.getBoundingClientRect();
      const size = canvasRef.current?.width || 150;

      let offsetX = e.clientX - rect.left;
      let offsetY = e.clientY - rect.top;

      // Scale from canvas size to 0-100 range
      let xValue = Math.floor((offsetX / size) * 100);
      let yValue = Math.floor(100 - (offsetY / size) * 100);
      Logger.debug("CANVAS clicked", xValue, yValue);

      let newValue = { ...value };
      newValue.x = xValue;
      newValue.y = yValue;

      if (!isControlled) setInternalValue(newValue);
      onChange(content.key, newValue);

      handleCanvasValue(offsetX, offsetY, xValue, yValue);
    } else if (!isControlled) {
      setInternalValue(defaultValue?.x ? defaultValue : {});
    }
  };

  const handleFeedbackChange = (e) => {
    if (!!onChange) {
      let newValue = { ...value };
      newValue.feedback = e.target.value;
      if (!isControlled) setInternalValue(newValue);
      onChange(content.key, newValue);
    } else if (!isControlled) {
      setInternalValue(defaultValue?.x ? defaultValue : {});
    }
  };

  const canvasSize = compact ? 100 : 150;

  useEffect(() => {
    const source = isControlled ? valueProp : defaultValue;
    if (source?.x != null && canvasRef.current) {
      let xValue = source.x;
      let yValue = source.y;

      // Scale from 0-100 range to current canvas size
      let offsetX = (xValue / 100) * canvasSize;
      let offsetY = canvasSize - (yValue / 100) * canvasSize;

      handleCanvasValue(offsetX, offsetY, xValue, yValue);
    }
    if (!isControlled) {
      setInternalValue(defaultValue || {});
      // Only propagate non-empty values upward. Calling onChange with an empty
      // defaultValue on mount/re-render would overwrite previously saved data
      // in the parent's userData if this component mounts before the restored
      // value has propagated down as defaultValue.
      if (onChange && defaultValue?.x != null) onChange(content.key, defaultValue);
    }
  }, [isControlled, defaultValue, valueProp, canvasSize]);


  return (
    <div className={`twodgauge-detail ${compact ? "twodgauge-detail--compact" : ""}`}>
      {/* Label */}
      {label && <label className="twodgauge-main-label">{label}</label>}

      {/* Canvas Section */}
      <div className="twodgauge-canvas-section">
        <div className="twodgauge-canvas-container">
          <div className="twodgauge-yaxis-label">
            <span>{yAxisLabel}</span>
          </div>
          <div className="twodgauge-canvas-wrapper">
            <canvas
              ref={canvasRef}
              width={canvasSize}
              height={canvasSize}
              className={`twodgauge-canvas ${readOnly ? "twodgauge-canvas--readonly" : ""}`}
              onClick={handleCanvasClick}
              title={
                !textAreaVisible && finalPlaceholderMessage
                  ? finalPlaceholderMessage
                  : ""
              }
            />
            <div className="twodgauge-xaxis-label">
              <span>{xAxisLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      {enableFeedback && textAreaVisible && (
        <div className="twodgauge-feedback-section">
          {textAreaLabel && (
            <label className="twodgauge-feedback-label">{textAreaLabel}</label>
          )}
          <AutogrowTextarea
            minHeight={50}
            value={value.feedback}
            onChange={handleFeedbackChange}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  );
};

export default TwoDGauge;
